import mergeClasses from '../../../utils/shallowMerge';
import { Checkbox } from '../../atoms/checkbox/Checkbox';
import { Accordion } from '../accordion/Accordion';
import clsx from 'clsx';
import defaultClasses from './Filter.module.scss';
import React from 'react';
import { useSelector } from 'react-redux';
import { SIZES_SORT_ORDER } from '../../organisms/productActions/utils/sortOrder';
import { convertTextToId } from '@srcDS/utils/convertTextToId';
import CustomRange from '../customRange/CustomRange';
import { useFilter } from '@srcDS/tastic/pages/category/hooks/useFilter';

import { FilterFacet } from './Filter';
import { SizeSelector } from './sizeSelector/SizeSelector';

interface Term {
  handle: string;
  name: string;
  value: string;
  identifier: string;
  label: string;
  count: number;
  key: string;
  selected: boolean;
  isColor?: boolean;
}

interface LocalFilterFacet {
  type: 'term' | 'boolean' | 'range';
  terms?: Term[];
  selectedMin?: number;
  selectedMax?: number;
}

interface LocalFilters {
  [attributeId: string]: LocalFilterFacet;
}

interface UseFilterReturn {
  handleFacetChange: (filter: FilterFacet, status: boolean, termIndex: number, attributeId: string) => void;
  onPriceRangeChange: (facet: FilterFacet, min: number, max: number) => void;
  localFilters: LocalFilters;
}

interface RootState {
  app: {
    context: {
      locale: string;
    };
  };
}

interface CustomClasses {
  [key: string]: string;
}

interface MobileFiltersProps {
  customClasses?: CustomClasses | null;
  facets: FilterFacet[];
  isGlobalLoaderShow?: boolean;
  triggerRoute?: () => void;
}

export const sortedFacetsTerm = (attributeId: string, terms: Term[]): Term[] => {
  if (attributeId === 'variants.attributes.refinementcolour') return terms.map((term) => ({ ...term, isColor: true }));

  if (attributeId !== 'variants.attributes.size') return terms;

  return (
    terms?.sort((a, b) => SIZES_SORT_ORDER.indexOf(String(a.name)) - SIZES_SORT_ORDER.indexOf(String(b.name))) || []
  );
};

const accordionProps = {
  canOpenMultiple: true,
  isRotate: false,
  timeout: 200,
} as const;

const MobileFilters: React.FC<MobileFiltersProps> = ({
  customClasses = null,
  facets,
  isGlobalLoaderShow,
  triggerRoute,
}) => {
  const { handleFacetChange, onPriceRangeChange, localFilters }: UseFilterReturn = useFilter();

  if (!Array.isArray(facets) || !facets.length) {
    return null;
  }

  const locale = useSelector((state: RootState) => state.app.context.locale);
  const styles = mergeClasses(defaultClasses, customClasses) as Record<string, string>;

  const renderRange = (facet: FilterFacet): JSX.Element => {
    return <CustomRange facet={facet} onPriceRangeChange={onPriceRangeChange} />;
  };

  const getFacetTitle = (facet: FilterFacet): string => {
    if (facet.attributeId === 'variants.attributes.primarycategory') {
      return 'Category';
    }
    return facet.label[locale] || facet.label['en_AU@AUD'] || '';
  };

  const renderTerms = (facet: FilterFacet, index: number): JSX.Element | null => {
    const termsToRender: Term[] = localFilters[facet.attributeId]?.terms || facet.terms || [];
    const isSizeSelector = facet.attributeId === 'variants.attributes.size';

    return (
      <Accordion.Section
        key={`${facet.facetId}_${index}`}
        // @ts-ignore
        isOpen={facet.selected}
        title={facet.label[locale]}
        id={facet.facetId}
        customClasses={{ section: styles.section }}
      >
        <Accordion.Title
          isRotate={accordionProps.isRotate}
          timeout={accordionProps.timeout}
          data-filter={convertTextToId(facet.label[locale])}
          customClasses={{ content: styles.content, title: styles.title }}
        >
          {getFacetTitle(facet)}
        </Accordion.Title>

        <Accordion.Panel
          isNoDelay={true}
          isGlobalLoaderShow={isGlobalLoaderShow}
          triggerRoute={triggerRoute}
          customClasses={{
            wrapper: isSizeSelector ? styles.sizeSelectorWrapper : styles.wrapper,
            rangePanel: styles.rangePanel,
          }}
          staticMaxHeight={100}
          dynamicHeight
        >
          {facet.type === 'term' || facet.type === 'boolean'
            ? sortedFacetsTerm(facet.attributeId, termsToRender)?.map((it: Term, termIndex: number) => {
                const isSelected = localFilters[facet.attributeId]?.terms
                  ? (localFilters[facet.attributeId].terms.find((t) => t.value === it.value)?.selected ?? false)
                  : it.selected;

                if (isSizeSelector) {
                  return (
                    <SizeSelector
                      // @ts-ignore
                      key={`${facet.attributeId}_${termIndex}`}
                      label={it.label}
                      selected={isSelected}
                      onClick={(status) => {
                        handleFacetChange(facet, status, termIndex, facet.attributeId);
                      }}
                    />
                  );
                }

                return (
                  !!it.count && (
                    <div key={`${facet.attributeId}_${termIndex}`} className={styles.filtersList}>
                      <div className={styles.filterItems}>
                        {/* @ts-ignore */}
                        <Checkbox
                          variant="inverse"
                          checkStatus={isSelected}
                          customLabelStyle={styles.filterItemsLabel}
                          onChange={(status: boolean) => {
                            handleFacetChange(facet, status, termIndex, facet.attributeId);
                          }}
                          className={clsx(styles.checkbox)}
                          dataAttributes={{
                            'data-filter-attribute': convertTextToId(it.value),
                          }}
                        >
                          {it?.isColor && (
                            <div
                              className={styles.colorIndicator}
                              style={{
                                background:
                                  it.value.toLowerCase() === 'multi'
                                    ? 'conic-gradient(red, orange, yellow, green, blue, purple, red)'
                                    : it.value.toLowerCase() === 'cream'
                                      ? '#F5DFBD'
                                      : it.value.toLowerCase(),
                              }}
                            />
                          )}
                          <span>{it.value}</span>
                        </Checkbox>
                      </div>
                    </div>
                  )
                );
              })
            : facet.type === 'range'
              ? renderRange(facet)
              : null}
        </Accordion.Panel>
      </Accordion.Section>
    );
  };

  return (
    <Accordion
      canOpenMultiple={accordionProps.canOpenMultiple}
      customClasses={{ accordionWrapper: styles.accordionWrapper }}
    >
      {facets.map((facet: FilterFacet, index: number) => {
        const { label, type, terms } = facet || {};
        if ((label && type && terms?.length) || type === 'range') {
          return renderTerms(facet, index);
        }
        return null;
      })}
    </Accordion>
  );
};

export default MobileFilters;
